import React, { useRef } from 'react';
import { Box, Container, Typography, useTheme } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import HearingOutlinedIcon from '@mui/icons-material/HearingOutlined';
import StrategyOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { SectionProps } from '../types/SectionProps';

const ApproachSection: React.FC<SectionProps> = ({ id }) => {
  const theme = useTheme();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const approaches = [
    {
      title: 'Écoute',
      description: 'Comprendre avant de conseiller. Une attention sincère à vos besoins et objectifs pour construire une relation de confiance.',
      icon: <HearingOutlinedIcon sx={{ fontSize: 60, color: 'secondary.main' }} />,
      color: '#D5B97B',
      delay: 0.2
    },
    {
      title: 'Stratégie',
      description: 'Des solutions claires, concrètes et personnalisées pour vous guider vers les meilleures décisions patrimoniales.',
      icon: <StrategyOutlinedIcon sx={{ fontSize: 60, color: 'secondary.main' }} />,
      color: '#1E2A38',
      delay: 0.4
    },
    {
      title: 'Transmission',
      description: 'Un accompagnement humain et engagé pour construire un patrimoine pérenne et sécurisant pour l\'avenir.',
      icon: <PeopleAltOutlinedIcon sx={{ fontSize: 60, color: 'secondary.main' }} />,
      color: '#E63976',
      delay: 0.6
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, type: "spring", stiffness: 100 } }
  };
  
  const iconVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: { 
        type: "spring", 
        stiffness: 200, 
        damping: 15, 
        delay: 0.2 
      } 
    },
    hover: { 
      scale: 1.1,
      transition: { duration: 0.3, ease: "easeOut" }
    }
  };

  return (
    <Box
      id={id}
      ref={ref}
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: 'background.default',
        scrollMarginTop: '64px', // Pour le défilement fluide
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' },
              position: 'relative',
              display: 'inline-block'
            }}
          >
            Notre approche
            <Box 
              sx={{ 
                position: 'absolute',
                bottom: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '40px',
                height: '3px',
                bgcolor: 'error.main'
              }} 
            />
          </Typography>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              maxWidth: '700px', 
              mx: 'auto', 
              mt: 4,
              color: 'text.secondary',
              fontSize: '1.1rem'
            }}
          >
            Bienveillance, précision et simplicité guident chaque étape de notre accompagnement
            pour vous offrir une expérience sur-mesure et humaine.
          </Typography>
        </Box>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 4 }}>
            {approaches.map((approach, index) => (
              <Box key={index}>
                <motion.div 
                  variants={itemVariants}
                  whileHover={{ y: -10, transition: { duration: 0.3 } }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      position: 'relative',
                      padding: 4,
                      borderRadius: 2,
                      overflow: 'hidden',
                      backgroundColor: theme.palette.background.paper,
                      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.3s ease',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '4px',
                        backgroundColor: approach.color,
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '30%',
                        height: '1px',
                        backgroundColor: approach.color,
                        opacity: 0.3,
                      }
                    }}
                  >
                    <motion.div
                      variants={iconVariants}
                      whileHover="hover"
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '90px',
                        height: '90px',
                        borderRadius: '50%',
                        backgroundColor: `${approach.color}10`,
                        marginBottom: '24px',
                      }}
                    >
                      {approach.icon}
                    </motion.div>
                    
                    <Typography 
                      variant="h4" 
                      component="h3" 
                      sx={{ 
                        mb: 2,
                        fontSize: '1.5rem',
                        fontWeight: 600,
                        position: 'relative',
                        display: 'inline-block',
                      }}
                    >
                      {approach.title}
                    </Typography>
                    
                    <Typography 
                      sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.7,
                        fontSize: '1rem'
                      }}
                    >
                      {approach.description}
                    </Typography>
                  </Box>
                </motion.div>
              </Box>
            ))}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ApproachSection;
